import { createMap, createPickMarker } from "../map";
import { searchPlace, type GeoResult } from "../api";
import { escapeHtml } from "../format";

/** Mostra/nasconde lo stato di caricamento su un bottone del location picker:
 * spinner al posto dell'icona, `disabled` + `aria-busy` per gli screen reader. */
function setButtonLoading(btn: HTMLButtonElement, loading: boolean): void {
  btn.disabled = loading;
  if (loading) btn.setAttribute("aria-busy", "true");
  else btn.removeAttribute("aria-busy");
  btn.querySelector("svg.icon")?.toggleAttribute("hidden", loading);
  btn.querySelector(".spinner")?.toggleAttribute("hidden", !loading);
}

export interface LocationPicker {
  getLat(): number | null;
  getLng(): number | null;
  /** Imposta coordinate da una fonte esterna (EXIF) e ricentra la mappa. */
  setExternalCoords(lat: number, lng: number): void;
  /**
   * Carica nel picker le coordinate dell'elemento attivo (coda multi-upload):
   * imposta o rimuove il pin SENZA invocare onChange (non è una modifica
   * dell'utente, è il caricamento dello stato di una foto già in coda).
   */
  loadCoords(lat: number | null, lng: number | null): void;
  /** Stacca il listener di click sulla mappa e rimuove il pin. */
  destroy(): void;
}

/** Location picker condiviso upload/edit: click sulla mappa, pin trascinabile,
 * "Usa posizione attuale" e ricerca luogo esplicita via /api/geo (niente
 * autocomplete live: solo su bottone/Enter, min 3 caratteri, max 5 risultati). */
export function createLocationPicker(
  map: ReturnType<typeof createMap>,
  root: Element,
  placeInput: HTMLInputElement,
  initialLat: number | null,
  initialLng: number | null,
  // Notifica ogni modifica utente delle coordinate (click mappa, drag, geo,
  // ricerca). Usato dalla coda multi-upload per aggiornare la foto attiva.
  // Non passato dal flusso di edit/single: comportamento invariato.
  onChange?: (lat: number, lng: number) => void,
): LocationPicker {
  const coordEl = root.querySelector<HTMLSpanElement>(".loc-coord")!;
  const geoBtn = root.querySelector<HTMLButtonElement>(".loc-geo-btn")!;
  const searchInput = root.querySelector<HTMLInputElement>(".loc-search-input")!;
  const searchBtn = root.querySelector<HTMLButtonElement>(".loc-search-btn")!;
  const resultsEl = root.querySelector<HTMLUListElement>(".loc-results")!;
  const searchMsgEl = root.querySelector<HTMLParagraphElement>(".loc-msg--search")!;
  const geoMsgEl = root.querySelector<HTMLParagraphElement>(".loc-msg--geo")!;

  let lat = initialLat;
  let lng = initialLng;
  let marker: ReturnType<typeof createPickMarker> | null = null;

  function updateCoordLabel(): void {
    coordEl.textContent =
      lat !== null && lng !== null
        ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
        : "nessuna (tocca la mappa o usa la posizione)";
  }

  function setCoords(newLat: number, newLng: number): void {
    lat = newLat;
    lng = newLng;
    if (marker) marker.setLatLng([newLat, newLng]);
    else marker = createPickMarker(map, newLat, newLng, setCoords);
    updateCoordLabel();
    onChange?.(newLat, newLng);
  }

  if (lat !== null && lng !== null) marker = createPickMarker(map, lat, lng, setCoords);
  updateCoordLabel();

  const onMapClick = (e: { latlng: { lat: number; lng: number } }) => setCoords(e.latlng.lat, e.latlng.lng);
  map.on("click", onMapClick);

  geoBtn.addEventListener("click", () => {
    if (!navigator.geolocation) {
      geoMsgEl.textContent = "Geolocalizzazione non disponibile.";
      return;
    }
    setButtonLoading(geoBtn, true);
    geoMsgEl.textContent = "Rilevo la posizione…";
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords(pos.coords.latitude, pos.coords.longitude);
        map.setView([pos.coords.latitude, pos.coords.longitude], 15);
        geoMsgEl.textContent = "";
        setButtonLoading(geoBtn, false);
      },
      () => {
        geoMsgEl.textContent = "Impossibile ottenere la posizione.";
        setButtonLoading(geoBtn, false);
      },
    );
  });

  function clearResults(): void {
    resultsEl.hidden = true;
    resultsEl.innerHTML = "";
  }

  function renderResults(items: GeoResult[]): void {
    if (items.length === 0) {
      clearResults();
      return;
    }
    resultsEl.innerHTML = items
      .map((r, i) => `<li><button type="button" class="loc-results__item" data-idx="${i}">${escapeHtml(r.label)}</button></li>`)
      .join("");
    resultsEl.hidden = false;
    resultsEl.querySelectorAll<HTMLButtonElement>("[data-idx]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const r = items[Number(btn.dataset.idx)];
        setCoords(r.lat, r.lng);
        map.setView([r.lat, r.lng], 15);
        placeInput.value = r.label;
        clearResults();
        searchInput.value = "";
        searchMsgEl.textContent = "";
        searchInput.focus();
      });
    });
    // Sposta il focus sul primo risultato: utile per chi naviga da tastiera
    // dopo aver premuto Cerca/Enter senza dover ripartire dall'input.
    resultsEl.querySelector<HTMLButtonElement>("[data-idx]")?.focus();
  }

  let searching = false;
  async function runSearch(): Promise<void> {
    if (searching) return;
    const q = searchInput.value.trim();
    if (q.length < 3) {
      searchMsgEl.textContent = "Inserisci almeno 3 caratteri per cercare.";
      searchInput.focus();
      return;
    }
    searching = true;
    setButtonLoading(searchBtn, true);
    searchMsgEl.textContent = "Ricerca…";
    clearResults();
    try {
      const results = await searchPlace(q);
      renderResults(results);
      if (results.length === 0) {
        searchMsgEl.textContent = "Nessun risultato.";
        searchInput.focus();
      }
    } catch {
      searchMsgEl.textContent = "Errore durante la ricerca. Riprova.";
      searchInput.focus();
    } finally {
      searching = false;
      setButtonLoading(searchBtn, false);
    }
  }

  searchBtn.addEventListener("click", runSearch);
  searchInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void runSearch();
    } else if (e.key === "Escape") {
      if (!resultsEl.hidden || searchMsgEl.textContent) {
        e.preventDefault();
        clearResults();
        searchMsgEl.textContent = "";
      } else if (searchInput.value) {
        e.preventDefault();
        searchInput.value = "";
      }
    }
  });

  return {
    getLat: () => lat,
    getLng: () => lng,
    setExternalCoords(newLat, newLng) {
      setCoords(newLat, newLng);
      map.setView([newLat, newLng], 15);
    },
    loadCoords(newLat, newLng) {
      lat = newLat;
      lng = newLng;
      if (newLat !== null && newLng !== null) {
        if (marker) marker.setLatLng([newLat, newLng]);
        else marker = createPickMarker(map, newLat, newLng, setCoords);
        map.setView([newLat, newLng], 15);
      } else if (marker) {
        marker.remove();
        marker = null;
      }
      updateCoordLabel();
    },
    destroy() {
      map.off("click", onMapClick);
      if (marker) marker.remove();
    },
  };
}
